import { useEffect, useState } from 'react';

import { Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';

import { projectApis } from '../../../apis/projectApis';
import { solrApis } from '../../../apis/solrApis';
import AlertMessage from '../../../components/elements/alert-message/AlertMessage';
import Loading from '../../../components/elements/loading/Loading';
import Text from '../../../components/elements/text/Text';
import Title from '../../../components/elements/title/Title';
import ProjectForm from '../../../components/page-sections/project-form/ProjectForm';
import ProjectManager from '../../../components/page-sections/project-manager/ProjectManager';
import Breadcrumbs from '../../../components/sections/breadcrumbs/Breadcrumbs';
import DeleteButton from '../../../components/sections/delete/DeleteButton';
import TitlePanel from '../../../components/sections/title-panel/TitlePanel';
import { useProject } from '../../../contexts/ProjectContext';
import { handleErrors } from '../../../helpers';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { buildPath, PRIVATE_ROUTE } from '../../../routes/routes';
import { mapBlobFormData, sentenceCase } from '../../../utils/utils';

// Page
const ProjectSettingsPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const { project, refreshProject } = useProject();

  const [areInstances, setAreInstances] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [manager, setManager] = useState(null);

  const fetchProjectManager = async () => {
    try {
      const { data: managerRes } =
        await projectApis.getProjectManagers(projectId);
      setManager(managerRes?.content?.[0]);
    } catch (error) {
      handleErrors(navigate, error, showToast);
      setError(true);
    }
  };

  const fetchData = async (projectId) => {
    setLoading(true);
    try {
      const { data: instances } = await solrApis.getInstances(projectId);
      setAreInstances(instances?.content.length > 0);
      fetchProjectManager();
    } catch (error) {
      handleErrors(navigate, error, showToast);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // component events
  const onUpdate = async (data) => {
    const formData = mapBlobFormData(data, 'project');
    try {
      await projectApis.updateProject(projectId, formData);
      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('updateSuccess', {
            ns: 'notifications',
            text: t('projectInfo', { ns: 'project' }),
          })
        )
      );
      await refreshProject();
    } catch (error) {
      showToast(
        TOAST_TYPE.ERROR,
        sentenceCase(
          t('updateError', {
            ns: 'notifications',
            text: t('projectInfo', { ns: 'project' }),
          })
        )
      );
    }
  };

  const onRemoveImage = async () => {
    try {
      await projectApis.deleteProjectIcon(projectId);
      await refreshProject();
      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('deleteSuccess', {
            ns: 'notifications',
            text: t('avatar', { ns: 'fields' }),
          })
        )
      );
    } catch (error) {
      showToast(
        TOAST_TYPE.ERROR,
        sentenceCase(
          t('deleteError', {
            ns: 'notifications',
            text: t('avatar', { ns: 'fields' }),
          })
        )
      );
    }
  };

  const onUpdateManager = async (data) => {
    if (manager) {
      try {
        await projectApis.updateProjectManager(projectId, manager.id, data);
        showToast(
          TOAST_TYPE.SUCCESS,
          sentenceCase(
            t('updateSuccess', {
              ns: 'notifications',
              text: t('pm.info', { ns: 'project' }),
            })
          )
        );
      } catch (error) {
        showToast(
          TOAST_TYPE.ERROR,
          sentenceCase(
            t('updateError', {
              ns: 'notifications',
              text: t('pm.info', { ns: 'project' }),
            })
          )
        );
      } finally {
        await fetchProjectManager();
      }
    } else {
      try {
        await projectApis.addProjectManager(projectId, data);
        showToast(
          TOAST_TYPE.SUCCESS,
          sentenceCase(
            t('addSuccess', {
              ns: 'notifications',
              text: t('projectManager', { ns: 'sectionTitles' }),
            })
          )
        );
      } catch (error) {
        showToast(
          TOAST_TYPE.ERROR,
          sentenceCase(
            t('addError', {
              ns: 'notifications',
              text: t('projectManager', { ns: 'sectionTitles' }),
            })
          )
        );
      } finally {
        await fetchProjectManager();
      }
    }
  };

  const onDelete = () => {
    projectApis
      .deleteProject(projectId)
      .then(() => {
        showToast(
          TOAST_TYPE.SUCCESS,
          sentenceCase(
            t('deleteSuccess', {
              ns: 'notifications',
              text: t('project', { ns: 'project' }),
            })
          )
        );
        const projectsPage = buildPath({
          path: PRIVATE_ROUTE.PROJECTS,
        });
        setTimeout(() => navigate(projectsPage), 1000);
      })
      .catch(() => {
        showToast(
          TOAST_TYPE.ERROR,
          sentenceCase(
            t('deleteError', {
              ns: 'notifications',
              text: t('project', { ns: 'project' }),
            })
          )
        );
      });
  };

  useEffect(() => {
    fetchData(projectId);
  }, [projectId]);

  if (loading) return <Loading />;

  if (error)
    return (
      <AlertMessage
        severity="error"
        message={sentenceCase(
          t('loading', {
            ns: 'errors',
            content: t('settingsPage', {
              ns: 'project',
              text: t('project', { ns: 'project' }),
            }),
          })
        )}
      />
    );

  return (
    <Container>
      {project && (
        <>
          <Breadcrumbs />
          <TitlePanel title={t('settings', { ns: 'titles' })} />
          <ProjectForm
            data={project}
            onSubmit={onUpdate}
            handleRemoveImage={onRemoveImage}
          />
          <ProjectManager onSubmit={onUpdateManager} manager={manager} />
          <Title
            subTitle
            text={t('projectDeletion', { ns: 'sectionTitles' })}
          />
          <Text>
            {sentenceCase(
              t('deleteWarning', {
                ns: 'descriptions',
                text: t('project', { ns: 'project' }),
              })
            )}
          </Text>
          {areInstances ? (
            <AlertMessage
              severity="info"
              message={t('emptyInstanceBeforeDeleteProject', {
                ns: 'descriptions',
              })}
            />
          ) : (
            <DeleteButton
              buttonProps={{ sx: { mt: 2 } }}
              buttonText={t('deleteProject', { ns: 'buttons' })}
              onDelete={onDelete}
              validation={project.name}
              title={t('deleteProject', { ns: 'buttons' })}
              description={t('areYouSure', {
                ns: 'descriptions',
                text: t('project', { ns: 'project' }),
              })}
            />
          )}
        </>
      )}
    </Container>
  );
};

export default ProjectSettingsPage;
